import { postDataAPI } from './fetchData';

class UsageTracker {
  
  async track(actionType, metadata = {}) {
    try {
      const token = localStorage.getItem('dizeeToken');
      if (!token) return;
      
      await postDataAPI('usage/track', {
        action_type: actionType,
        metadata
      }, token);
    } catch (error) {
      console.error('❌ Usage tracking failed:', error);
    }
  }
  
  // Convenience methods for common actions
  async trackAIRequest(model = 'gpt-4o-mini', promptLength = 0, responseLength = 0) {
    await this.track('dizee_ai_request', { 
      model, 
      prompt_length: promptLength,
      response_length: responseLength,
      estimated_tokens: Math.ceil((promptLength + responseLength) / 3.5) // rough estimate
    });
  }
  
  async trackMusicStatsCall(endpoint, queryType = 'search', resultsCount = 0) {
    await this.track('music_statistics_call', { 
      endpoint, 
      query_type: queryType,
      results_count: resultsCount 
    });
  }
  
  async trackCatalogEntry(songCount = 1, source = 'manual') {
    const actionType = songCount > 10 ? 'bulk_import_operation' : 'catalog_entry_basic';
    await this.track(actionType, { 
      song_count: songCount,
      source 
    });
  }
  
  async trackAutoPopulation(source = 'spotify', itemsFound = 0) {
    await this.track('catalog_auto_population', {
      source,
      items_found: itemsFound
    });
  }
  
  async trackBookingCreation(bookingType = 'event') {
    await this.track('booking_creation', { booking_type: bookingType });
  }
  
  async trackMerchItem(productType = 'general') {
    await this.track('merch_item_addition', { product_type: productType });
  }
  
  async trackPageCreation(pageType = 'basic', moduleCount = 0) {
    const actionType = pageType === 'focus' ? 'focus_page_creation' : 'page_content_addition';
    await this.track(actionType, { 
      page_type: pageType,
      module_count: moduleCount 
    });
  }
  
  async trackModuleAddition(moduleType = 'custom') {
    const actionMap = {
      custom: 'module_addition_custom',
      music: 'module_addition_music',
      video: 'module_addition_video',
      event: 'module_addition_event',
      product: 'module_addition_product',
      playlist: 'module_addition_playlist',
      social_feed: 'module_addition_social_feed'
    };
    
    const actionType = actionMap[moduleType] || 'module_addition_custom';
    await this.track(actionType, { module_type: moduleType });
  }
  
  async trackPlaylisting(searchType = 'discovery', platform = 'spotify', resultsCount = 0) {
    await this.track('playlisting_search', {
      search_type: searchType,
      platform,
      results_count: resultsCount
    });
  }
  
  async trackSocialListening(platform = 'all', mentionsFound = 0) {
    await this.track('social_listening_query', {
      platform,
      mentions_found: mentionsFound
    });
  }
  
  async trackCharting(trackCount = 1, chartType = 'general') {
    await this.track('charting_data_pull', {
      track_count: trackCount,
      chart_type: chartType
    });
  }
  
  async trackSpotifyRevenue(analysisType = 'basic') {
    await this.track('spotify_revenue_analysis', {
      analysis_type: analysisType
    });
  }
  
  async trackTeamInvitation(role = 'member') {
    await this.track('team_member_invitation', { role });
  }
  
  async trackDataExport(exportType = 'csv', recordCount = 0) {
    await this.track('data_export_operation', {
      export_type: exportType,
      record_count: recordCount
    });
  }
  
  async trackFileUpload(fileType = 'image', fileSize = 0) {
    const actionType = fileType === 'csv' ? 'csv_upload_processing' : 'image_upload';
    await this.track(actionType, {
      file_type: fileType,
      file_size: fileSize
    });
  }
  
  async trackFileDownload(fileSize = 0) {
    let actionType = 'file_download_small';
    if (fileSize > 10 * 1024 * 1024) { // 10MB+
      actionType = 'large_file_download';
    } else if (fileSize > 1024 * 1024) { // 1MB+
      actionType = 'medium_file_download';
    }
    
    await this.track(actionType, { file_size: fileSize });
  }
  
  // Low usage tracking (essentially free)
  async trackBasicAction(actionType, metadata = {}) {
    const lowUsageActions = [
      'basic_analytics_view',
      'profile_update',
      'settings_change',
      'page_publishing',
      'content_editing',
      'content_deletion',
      'social_links_update',
      'theme_color_change',
      'basic_navigation'
    ];
    
    if (lowUsageActions.includes(actionType)) {
      await this.track(actionType, metadata);
    }
  }
  
  // Batch tracking for multiple actions
  async trackBatch(actions) {
    for (const action of actions) {
      await this.track(action.type, action.metadata);
    }
  }
}

export default new UsageTracker(); 